import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/LanguageContext";
import { useSimulation } from "@/context/SimulationContext";
import { LANGUAGES } from "@/i18n/translations";
import { ArrowRight, Layers, Zap, Globe2 } from "lucide-react";

export default function HomeHero() {
  const { t } = useI18n();
  const { resetSimulation } = useSimulation();

  const handleStart = () => {
    resetSimulation();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 relative">
      {/* Grid backdrop */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[color:var(--brand)]" />
            <span className="text-xs font-mono uppercase tracking-[0.24em] text-[color:var(--brand)]">
              {t("home.heroTag")}
            </span>
          </div>

          <h1 className="font-display font-black text-[color:var(--text)] text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
            {t("home.heroTitle")}
          </h1>

          <p className="mt-6 text-[color:var(--text-dim)] text-lg max-w-xl">
            {t("home.heroBody")}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link
              to="/simulator"
              onClick={handleStart}
              data-testid="home-start-btn"
              className="inline-flex items-center justify-center gap-2 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dim)] text-black font-semibold h-14 px-7 rounded transition-colors group"
            >
              {t("home.startBtn")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/saved"
              data-testid="home-view-saved-btn"
              className="inline-flex items-center justify-center gap-2 border border-[color:var(--border-strong)] hover:border-[color:var(--brand)] text-[color:var(--text)] h-14 px-7 rounded transition-colors"
            >
              {t("home.viewSavedBtn")}
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-2 flex items-center gap-2">
                <Zap className="w-3 h-3 text-[color:var(--brand)]" />
                {t("home.stat1")}
              </div>
              <div className="font-display font-bold text-3xl text-[color:var(--text)]">€ 285</div>
              <div className="text-xs text-[color:var(--text-dim)]">{t("home.per")}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-2 flex items-center gap-2">
                <Layers className="w-3 h-3 text-[color:var(--brand)]" />
                {t("home.stat2")}
              </div>
              <div className="font-display font-bold text-3xl text-[color:var(--text)]">11,67 h</div>
              <div className="text-xs text-[color:var(--text-dim)]">{t("home.per")}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-mute)] mb-2 flex items-center gap-2">
                <Globe2 className="w-3 h-3 text-[color:var(--brand)]" />
                {t("home.stat3")}
              </div>
              <div className="font-display font-bold text-3xl text-[color:var(--text)]">{LANGUAGES.length}</div>
              <div className="text-xs text-[color:var(--text-dim)]">{LANGUAGES.map((l) => l.flag).join(" · ")}</div>
            </div>
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5">
          <div className="card-tech relative overflow-hidden aspect-[4/5] max-w-md ml-auto">
            <img
              src="https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxyZWZyaWdlcmF0ZWQlMjB0cnVjayUyMHRyYWlsZXJ8ZW58MHx8fHwxNzg1MzM0MDQ2fDA&ixlib=rb-4.1.0&q=85"
              alt="Refrigerated trailer"
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[color:var(--brand)] mb-3">
                <span className="w-6 h-px bg-[color:var(--brand)]" />
                back door gasket system
              </div>
              <div className="font-display font-bold text-white text-2xl leading-tight">
                Primo
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 font-mono text-xs text-[color:var(--text-dim)]">
                <div>
                  <div className="text-[color:var(--text-mute)] text-[10px] uppercase">Standard door</div>
                  <div className="text-white">2600 × 1200 mm</div>
                </div>
                <div>
                  <div className="text-[color:var(--text-mute)] text-[10px] uppercase">Sealing</div>
                  <div className="text-white">85mm profile</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
